import React, { useEffect, useState } from "react";

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 200;

const MERGE_FIELDS = [
  { key: "first_name", label: "First name" },
  { key: "middle_name", label: "Middle name" },
  { key: "last_name", label: "Last name" },
  { key: "date_of_birth", label: "Date of birth" },
  { key: "sex", label: "Sex" },
  { key: "primary_language", label: "Primary language" },
  { key: "referral_source", label: "Referral source" },
  { key: "notes", label: "Notes" },
] as const;

type MergeFieldKey = (typeof MERGE_FIELDS)[number]["key"];
type MergeFieldValues = Record<string, string | null | undefined>;
type RecordCounts = Record<string, number | undefined>;

interface PatientSummary {
  id: number | string;
  fullName: string;
  patientPath: string;
  dateOfBirth?: string | null;
  primaryPhone?: string | null;
  primaryEmail?: string | null;
  tebraPatientId?: number | string | null;
  mergeFields?: MergeFieldValues;
  recordCounts?: RecordCounts;
  mergeWarnings?: string[];
  mergeBlockedReason?: string | null;
}

interface ExistingMerge {
  id: number | string;
  mergedAtLabel: string;
  mergedByLabel?: string | null;
  sourcePatient: PatientSummary;
}

interface SearchResponse {
  patients?: PatientSummary[];
}

interface PatientMergeWorkspaceProps {
  authenticityToken: string;
  currentPatient: PatientSummary;
  existingMerges?: ExistingMerge[];
  mergeUrl: string;
  searchUrl: string;
  unmergeUrl: string;
}

function formatFieldValue(key: string, value?: string | null): string {
  if (!value) return "Not recorded";

  if (key === "date_of_birth") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  }

  return value;
}

function patientMeta(patient: PatientSummary): string[] {
  return [
    patient.dateOfBirth ? `DOB ${patient.dateOfBirth}` : null,
    patient.primaryPhone || null,
    patient.primaryEmail || null,
    patient.tebraPatientId ? `Tebra ${patient.tebraPatientId}` : null,
  ].filter((value): value is string => Boolean(value));
}

function recordCountSummary(recordCounts?: RecordCounts): string[] {
  if (!recordCounts) return [];

  return [
    recordCounts.contacts ? `${recordCounts.contacts} contacts` : null,
    recordCounts.identities ? `${recordCounts.identities} identities` : null,
    recordCounts.communications ? `${recordCounts.communications} communications` : null,
    recordCounts.consults ? `${recordCounts.consults} consults` : null,
    recordCounts.documents ? `${recordCounts.documents} documents` : null,
    recordCounts.notes ? `${recordCounts.notes} notes` : null,
    recordCounts.operations ? `${recordCounts.operations} operations` : null,
    recordCounts.timelineEntries ? `${recordCounts.timelineEntries} timeline entries` : null,
  ].filter((value): value is string => Boolean(value));
}

export default function PatientMergeWorkspace({
  authenticityToken,
  currentPatient,
  existingMerges = [],
  mergeUrl,
  searchUrl,
  unmergeUrl,
}: PatientMergeWorkspaceProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PatientSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedPatientId, setSelectedPatientId] = useState<string>("");
  const [fieldSelections, setFieldSelections] = useState<Record<string, "current" | "candidate">>({});

  const trimmedQuery = query.trim();
  const currentFields = currentPatient.mergeFields || {};
  const selectedPatient = results.find((patient) => String(patient.id) === selectedPatientId) || null;

  useEffect(() => {
    if (!selectedPatient) {
      setFieldSelections({});
      return;
    }

    const nextSelections: Record<string, "current" | "candidate"> = {};
    MERGE_FIELDS.forEach(({ key }) => {
      const currentValue = currentFields[key];
      const candidateValue = selectedPatient.mergeFields?.[key];
      nextSelections[key] = currentValue ? "current" : candidateValue ? "candidate" : "current";
    });
    setFieldSelections(nextSelections);
  }, [currentFields, selectedPatient]);

  useEffect(() => {
    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setLoading(false);
      setErrorMessage("");
      return undefined;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      setLoading(true);
      setErrorMessage("");

      try {
        const response = await fetch(`${searchUrl}?q=${encodeURIComponent(trimmedQuery)}`, {
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Search failed with status ${response.status}`);
        }

        const payload: SearchResponse = await response.json();
        setResults(Array.isArray(payload.patients) ? payload.patients : []);
      } catch (error: unknown) {
        if (error instanceof Error && error.name === "AbortError") return;

        setResults([]);
        setErrorMessage("Patient search is unavailable right now.");
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [searchUrl, trimmedQuery]);

  let resultSummary = "Search for a duplicate chart by name, phone, email, or Tebra ID.";
  if (trimmedQuery.length >= MIN_QUERY_LENGTH) {
    if (loading) {
      resultSummary = "Searching patients...";
    } else if (errorMessage) {
      resultSummary = errorMessage;
    } else if (results.length === 0) {
      resultSummary = "No duplicate candidates found.";
    } else {
      resultSummary = `${results.length} candidate${results.length === 1 ? "" : "s"} found.`;
    }
  }

  return (
    <div className="space-y-4">
      <section className="app-surface p-4 md:p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <p className="app-kicker">Duplicate resolution</p>
            <h2 className="mt-0.5 text-xl font-semibold text-neutral-950 dark:text-white">Merge or unmerge patient charts</h2>
            <p className="mt-2 max-w-3xl text-sm text-neutral-600 dark:text-neutral-300">
              Merge another patient record into this chart when both records represent the same person. Unmerge only replays
              tracked merges created from this workspace.
            </p>
          </div>

          <div className="app-meta-card">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400">Current survivor chart</p>
            <p className="mt-1 text-base font-semibold text-neutral-950 dark:text-white">{currentPatient.fullName}</p>
            {patientMeta(currentPatient).length > 0 ? (
              <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">{patientMeta(currentPatient).join(" | ")}</p>
            ) : null}
          </div>
        </div>

        {existingMerges.length > 0 ? (
          <div className="mt-4 rounded-lg border border-amber-200/80 bg-amber-50/70 p-3 dark:border-amber-900/60 dark:bg-amber-950/30">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-300">Merged charts</p>
                <p className="mt-1 text-sm text-amber-900 dark:text-amber-100">
                  {existingMerges.length} absorbed patient chart{existingMerges.length === 1 ? "" : "s"} can be restored from here.
                </p>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {existingMerges.map((merge) => (
                <article
                  key={merge.id}
                  className="rounded-lg border border-amber-200/80 bg-white/80 p-3 dark:border-amber-900/60 dark:bg-neutral-950/60"
                >
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <p className="text-base font-semibold text-neutral-950 dark:text-white">{merge.sourcePatient.fullName}</p>
                      {patientMeta(merge.sourcePatient).length > 0 ? (
                        <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">{patientMeta(merge.sourcePatient).join(" | ")}</p>
                      ) : null}
                      <p className="mt-2 text-xs uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                        Merged {merge.mergedAtLabel}
                        {merge.mergedByLabel ? ` by ${merge.mergedByLabel}` : ""}
                      </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <a href={merge.sourcePatient.patientPath} className="app-button-secondary">
                        Open old chart
                      </a>
                      <form method="post" action={unmergeUrl}>
                        <input type="hidden" name="authenticity_token" value={authenticityToken} />
                        <input type="hidden" name="merge_id" value={merge.id} />
                        <button
                          type="submit"
                          className="app-button-secondary"
                          data-turbo-confirm={`Restore ${merge.sourcePatient.fullName} as its own patient chart?`}
                        >
                          Unmerge
                        </button>
                      </form>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        ) : null}
      </section>

      <section className="app-surface p-4 md:p-5">
        <div className="grid gap-5 xl:grid-cols-[minmax(0,0.9fr),minmax(0,1.1fr)]">
          <div>
            <label htmlFor="patient-merge-search" className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
              Find duplicate chart
            </label>
            <input
              id="patient-merge-search"
              type="search"
              value={query}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
              placeholder="Search name, phone, or email"
              autoComplete="off"
              className="app-input"
            />
            <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">{resultSummary}</p>

            <div className="mt-4 space-y-3">
              {results.map((patient) => {
                const selected = String(patient.id) === selectedPatientId;
                const blocked = Boolean(patient.mergeBlockedReason);

                return (
                  <article
                    key={patient.id}
                    className={`rounded-lg border p-3 transition ${
                      selected
                        ? "border-emerald-300 bg-emerald-50/60 dark:border-emerald-900/60 dark:bg-emerald-950/20"
                        : "border-neutral-200/80 bg-neutral-50/80 dark:border-neutral-800 dark:bg-neutral-950/60"
                    }`}
                  >
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                      <div>
                        <p className="text-base font-semibold text-neutral-950 dark:text-white">{patient.fullName}</p>
                        {patientMeta(patient).length > 0 ? (
                          <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">{patientMeta(patient).join(" | ")}</p>
                        ) : null}
                        {recordCountSummary(patient.recordCounts).length > 0 ? (
                          <p className="mt-2 text-xs uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">
                            {recordCountSummary(patient.recordCounts).join(" • ")}
                          </p>
                        ) : null}
                        {patient.mergeBlockedReason ? (
                          <p className="mt-3 text-sm font-medium text-rose-700 dark:text-rose-300">{patient.mergeBlockedReason}</p>
                        ) : null}
                        {patient.mergeWarnings && patient.mergeWarnings.length > 0 ? (
                          <ul className="mt-3 space-y-1 text-sm text-amber-700 dark:text-amber-300">
                            {patient.mergeWarnings.map((warning) => (
                              <li key={warning}>{warning}</li>
                            ))}
                          </ul>
                        ) : null}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <a href={patient.patientPath} className="app-button-secondary">
                          Open chart
                        </a>
                        <button
                          type="button"
                          className={blocked ? "app-button-secondary opacity-60" : "app-button-primary"}
                          disabled={blocked}
                          onClick={() => setSelectedPatientId(String(patient.id))}
                        >
                          {selected ? "Reviewing" : "Review merge"}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <div className="app-surface-muted p-3 md:p-4">
            {selectedPatient ? (
              <form method="post" action={mergeUrl} className="space-y-5">
                <input type="hidden" name="authenticity_token" value={authenticityToken} />
                <input type="hidden" name="merge[source_patient_id]" value={selectedPatient.id} />

                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">Merge review</p>
                  <h3 className="mt-0.5 text-lg font-semibold text-neutral-950 dark:text-white">
                    Merge {selectedPatient.fullName} into {currentPatient.fullName}
                  </h3>
                  <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">
                    Pick the survivor values you want to keep on the current chart before the merge is applied.
                  </p>
                </div>

                {selectedPatient.mergeWarnings && selectedPatient.mergeWarnings.length > 0 ? (
                  <div className="rounded-lg border border-amber-200/80 bg-amber-50/80 p-3 dark:border-amber-900/60 dark:bg-amber-950/30">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-300">Warnings</p>
                    <ul className="mt-3 space-y-2 text-sm text-amber-900 dark:text-amber-100">
                      {selectedPatient.mergeWarnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {selectedPatient.mergeBlockedReason ? (
                  <div className="rounded-lg border border-rose-200/80 bg-rose-50/80 p-3 dark:border-rose-900/60 dark:bg-rose-950/30">
                    <p className="text-sm font-medium text-rose-700 dark:text-rose-300">{selectedPatient.mergeBlockedReason}</p>
                  </div>
                ) : null}

                <div className="space-y-4">
                  {MERGE_FIELDS.map((field) => {
                    const currentValue = currentFields[field.key];
                    const candidateValue = selectedPatient.mergeFields?.[field.key];
                    const selection = fieldSelections[field.key] || "current";
                    const selectedValue = selection === "candidate" ? candidateValue || "" : currentValue || "";
                    const valuesDiffer = (currentValue || "") !== (candidateValue || "");

                    return (
                      <div key={field.key} className="rounded-lg border border-neutral-200/80 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-950/70">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold text-neutral-950 dark:text-white">{field.label}</p>
                          <span className="text-xs uppercase tracking-[0.14em] text-neutral-400 dark:text-neutral-500">
                            {valuesDiffer ? "Conflict" : "Same value"}
                          </span>
                        </div>

                        <div className="mt-3 grid gap-3 lg:grid-cols-2">
                          <label className="rounded-md border border-neutral-200/80 bg-neutral-50/80 p-3 dark:border-neutral-800 dark:bg-neutral-900/60">
                            <span className="flex items-center justify-between gap-3">
                              <span className="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">Keep current chart</span>
                              <input
                                type="radio"
                                name={`field-choice-${field.key}`}
                                checked={selection === "current"}
                                onChange={() =>
                                  setFieldSelections((previous) => ({ ...previous, [field.key]: "current" }))
                                }
                              />
                            </span>
                            <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-700 dark:text-neutral-200">
                              {formatFieldValue(field.key, currentValue)}
                            </p>
                          </label>

                          <label className="rounded-md border border-neutral-200/80 bg-neutral-50/80 p-3 dark:border-neutral-800 dark:bg-neutral-900/60">
                            <span className="flex items-center justify-between gap-3">
                              <span className="text-xs font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-neutral-400">Keep source chart</span>
                              <input
                                type="radio"
                                name={`field-choice-${field.key}`}
                                checked={selection === "candidate"}
                                onChange={() =>
                                  setFieldSelections((previous) => ({ ...previous, [field.key]: "candidate" }))
                                }
                              />
                            </span>
                            <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-700 dark:text-neutral-200">
                              {formatFieldValue(field.key, candidateValue)}
                            </p>
                          </label>
                        </div>

                        <input type="hidden" name={`merge[selected_attributes][${field.key}]`} value={selectedValue} />
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="submit"
                    className="app-button-primary"
                    disabled={Boolean(selectedPatient.mergeBlockedReason)}
                    data-turbo-confirm={`Merge ${selectedPatient.fullName} into ${currentPatient.fullName}?`}
                  >
                    Merge patient charts
                  </button>
                  <button type="button" className="app-button-secondary" onClick={() => setSelectedPatientId("")}>
                    Clear selection
                  </button>
                </div>
              </form>
            ) : (
              <div className="flex h-full min-h-72 items-center justify-center rounded-lg border border-dashed border-neutral-300/80 bg-white/70 p-5 text-center dark:border-neutral-700 dark:bg-neutral-950/40">
                <div>
                  <p className="text-sm font-medium text-neutral-600 dark:text-neutral-300">Pick a duplicate chart from the search results.</p>
                  <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
                    The review panel will show merge warnings and let you choose which values should survive on the current chart.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
